import { FloppyDiskIcon } from '@phosphor-icons/react';
import { format } from 'date-fns/format';
import { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { deepDifference } from '@/helpers';
import { PageHeader } from '@/modules/common';
import LoadingContainer from '@/modules/common/components/loading-container';
import { Routes } from '@/router/routes';

import { useMaskDetails } from '../api';
import { MaskForm } from '../components';
import { Mask } from '../types';
import { MaskFormValues } from '../validations';

export const MaskDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();

  const {
    details,
    isFetching,
    getUploadUrlDataMutation,
    updateMaskMutation,
    processMaskMutation,
  } = useMaskDetails(params.id);

  const defaultValues = useMemo(() => {
    if (!details) return null;

    return {
      ...details,
      maskUrl: details.imageFileObject.readUrl || '',
      updatedAt: format(new Date(details.updatedAt), 'yyyy-MM-dd'),
    };
  }, [details]);

  const updateMask = async (values: MaskFormValues) => {
    try {
      const body = deepDifference<Mask>(values, defaultValues);

      if (values.maskUrl instanceof File) {
        await getUploadUrlDataMutation.mutateAsync({
          file: values.maskUrl,
        });

        await processMaskMutation.mutateAsync();
      }

      await updateMaskMutation.mutateAsync({
        ...body,
        id: params.id,
      });
      toast.success(`Mask “${values.name}” updated successfully!`);
    } catch (error) {
      console.error(error.message);
    }
  };

  return (
    <LoadingContainer loading={isFetching}>
      <MaskForm editMode onSubmit={updateMask} defaultValues={defaultValues}>
        <PageHeader
          title={details?.name}
          asChildPage
          backPath={Routes.Masks}
          actions={[
            <Button
              key="save-mask"
              type="submit"
              data-testid="save-event-mask"
              isLoading={
                updateMaskMutation.isPending || processMaskMutation.isPending
              }
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      </MaskForm>
    </LoadingContainer>
  );
};
